import { gatewayApi } from '@/lib/hrApi';
import type { ConnectorStatus } from '@/features/pages/attendance/connectorHealth';

// Attendance review — talks to attendance-service via the gateway
// (/api/v1/attendance/*). gatewayApi attaches the access token + handles refresh.
const BASE = '/api/v1/attendance';

// ---- Types ----------------------------------------------------------------

export type BatchStatus = 'staged' | 'approved' | 'discarded';
export type DayStatus = 'pending_review' | 'approved' | 'excluded';
export type DayKind = 'workday' | 'rest_day' | 'public_holiday';
export type ExceptionSeverity = 'info' | 'warning' | 'critical';
export type ExceptionStatus = 'open' | 'resolved' | 'ignored';
export type OvertimeStatus = 'pending' | 'approved' | 'rejected';

export interface AttendanceBatch {
  id: string;
  source: string;
  file_name: string;
  period_start: string;
  period_end: string;
  status: BatchStatus;
  total_rows: number;
  matched_rows: number;
  unmatched_rows: number;
  exception_count: number;
  approved_at?: string | null;
  created_at: string;
}

export interface AttendanceDay {
  id: string;
  employee_id: string;
  /** Self check-in only: where the punch came from. Null = not checked. */
  clock_in_network?: 'office' | 'outside' | 'unknown' | null;
  clock_out_network?: 'office' | 'outside' | 'unknown' | null;
  work_date: string;
  clock_in?: string | null;
  clock_out?: string | null;
  punch_count: number;
  gross_minutes: number;
  break_minutes: number;
  net_minutes: number;
  regular_minutes: number;
  potential_ot_minutes: number;
  approved_ot_minutes: number;
  late_minutes: number;
  early_departure_minutes: number;
  day_kind: DayKind;
  status: DayStatus;
  source: string;
  batch_id?: string | null; // null = batchless (self check-in / manual)
}

export interface AttendanceException {
  id: string;
  employee_id: string;
  work_date: string;
  type: string;
  severity: ExceptionSeverity;
  message: string;
  status: ExceptionStatus;
  resolution_note?: string | null;
  created_at: string;
}

export interface OvertimeRequest {
  id: string;
  employee_id: string;
  work_date: string;
  minutes_requested: number;
  minutes_approved?: number | null;
  multiplier: number;
  status: OvertimeStatus;
  rejection_reason?: string | null;
  sent_to_payroll_at?: string | null;
}

export interface AttendanceBatchDetail extends AttendanceBatch {
  days: AttendanceDay[];
  exceptions: AttendanceException[];
  overtime_requests: OvertimeRequest[];
}

export interface ApproveBatchResult {
  status: string;
  approved_days: number;
  overtime_sent: number;
  overtime_pending: number;
}

export interface AttendancePolicy {
  work_start: string; // 'HH:MM'
  work_end: string; // 'HH:MM'
  grace_minutes: number;
  standard_daily_minutes: number;
  lunch_minutes: number;
  lunch_paid: boolean;
  auto_lunch_deduct: boolean;
  lunch_after_minutes: number;
  overtime_requires_approval: boolean;
  overtime_daily_cap_minutes: number;
  /** 0=Mon .. 6=Sun */
  rest_days: number[];
  // HR Action Center attendance intervention thresholds.
  intervention_enabled?: boolean;
  intervention_window_days?: number;
  lateness_review_threshold?: number;
  absence_review_threshold?: number;
  // Live connector (BioTime) only — file imports are not affected.
  notify_late?: boolean;
  notify_early_departure?: boolean;
  overnight_mode?: OvernightMode;
  overnight_max_shift_hours?: number;
  /** Go-live date, set by connector enrolment; read-only here. */
  measurement_start_date?: string | null;
}

/** split = each calendar day on its own; stitch_start / stitch_end = a shift
 *  crossing midnight belongs to the day it began / ended. */
export type OvernightMode = 'split' | 'stitch_start' | 'stitch_end';

// ---- Helpers ---------------------------------------------------------------

const data = <T,>(p: Promise<{ data: T }>) => p.then((r) => r.data);

/** 495 -> "8h 15m"; 0/undefined -> "0m". */
export const formatMinutes = (minutes?: number | null): string => {
  const m = Math.round(minutes ?? 0);
  if (!m) return '0m';
  const h = Math.floor(Math.abs(m) / 60);
  const rest = Math.abs(m) % 60;
  const sign = m < 0 ? '-' : '';
  if (h === 0) return `${sign}${rest}m`;
  if (rest === 0) return `${sign}${h}h`;
  return `${sign}${h}h ${rest}m`;
};

// ---- Batches ---------------------------------------------------------------

export const listBatches = () =>
  data<{ batches: AttendanceBatch[] }>(gatewayApi.get(`${BASE}/batches`)).then((r) => r.batches ?? []);

export const getBatch = (id: string) =>
  data<AttendanceBatchDetail>(gatewayApi.get(`${BASE}/batches/${id}`));

/** 409 with a detail message while critical exceptions are still open. */
export const approveBatch = (id: string) =>
  data<ApproveBatchResult>(gatewayApi.post(`${BASE}/batches/${id}/approve`));

export const discardBatch = (id: string) =>
  data<unknown>(gatewayApi.post(`${BASE}/batches/${id}/discard`));

/** Delete a batch and everything it brought in — scans, days, exceptions,
 *  overtime. Discard only sets a batch aside; this is how a batch that
 *  imported the wrong file is actually cleaned up. 409 with a reason when
 *  anything downstream already relies on it. */
export const rollbackBatch = (id: string) =>
  data<{ deleted: number; days_deleted: number; exceptions_deleted: number }>(
    gatewayApi.post(`${BASE}/batches/${id}/rollback`),
  );

// ---- Exceptions ------------------------------------------------------------

export interface ExceptionQueue {
  exceptions: AttendanceException[];
  /** How many match the filter — the page is a window, not the whole queue. */
  total: number;
  /** 'type/severity' -> count, so a screen can say "222 unmatched_employee". */
  counts: Record<string, number>;
}

export interface ExceptionQuery {
  status?: ExceptionStatus | 'all';
  type?: string;
  severity?: ExceptionSeverity;
  from?: string;
  to?: string;
  batch_id?: string;
  limit?: number;
  offset?: number;
}

/** The queue with its totals. Older callers that only want the rows should use
 *  `listExceptions`, which still resolves to an array. */
export const queryExceptions = (q: ExceptionQuery = {}) =>
  data<ExceptionQueue | AttendanceException[]>(
    gatewayApi.get(`${BASE}/exceptions`, { params: { status: 'open', ...q } }),
  ).then<ExceptionQueue>((r) =>
    Array.isArray(r)
      ? { exceptions: r, total: r.length, counts: {} }
      : { exceptions: r.exceptions ?? [], total: r.total ?? (r.exceptions ?? []).length, counts: r.counts ?? {} },
  );

export const listExceptions = (status: ExceptionStatus | 'all' = 'open') =>
  queryExceptions({ status }).then((r) => r.exceptions);

export const resolveException = (id: string, status: 'resolved' | 'ignored', note?: string) =>
  data<AttendanceException>(gatewayApi.post(`${BASE}/exceptions/${id}/resolve`, { status, ...(note ? { note } : {}) }));

/** Close a whole class of findings at once. The backend refuses an unscoped
 *  call (severity alone is not a scope) and requires the note — mass closing a
 *  review queue is something someone has to answer for later. */
export const bulkResolveExceptions = (
  scope: { ids?: string[]; type?: string; batch_id?: string; severity?: ExceptionSeverity; from?: string; to?: string },
  status: 'resolved' | 'ignored',
  note: string,
) => data<{ resolved: number; status: string }>(
  gatewayApi.post(`${BASE}/exceptions/bulk-resolve`, { status, note, ...scope }),
);

// ---- Insights (slice 1: trust, then cost) ----------------------------------

export interface AttendanceTrust {
  employees: number;
  working_days: number;
  expected_days: number;
  recorded_days: number;
  leave_days: number;
  covered_days: number;
  coverage_pct: number;
  verdict: 'good' | 'partial' | 'poor' | 'no_data';
  /** False means every total below is a FLOOR — say "at least", never imply
   *  the figure is the whole month. */
  totals_are_complete: boolean;
  roster_available: boolean;
  covered_through: string | null;
  open_exceptions: number;
  open_critical_exceptions: number;
  employees_without_a_record: string[];
  names_without_a_record: string[];
}

export interface OvertimeMonth {
  month: string;
  approved_minutes: number;
  pending_minutes: number;
  rejected_minutes: number;
  /** Approved and never sent to payroll — the failure that looks like success. */
  unsent_minutes: number;
}

export interface AttendanceBranchRow {
  branch_id: string | null;
  employees: number;
  expected_days: number;
  covered_days: number;
  coverage_pct: number;
  verdict: AttendanceTrust['verdict'];
  approved_minutes: number;
  pending_minutes: number;
}

export interface AttendanceTimeUse {
  recorded_days: number;
  workdays: number;
  worked_minutes: number;
  standard_minutes: number;
  shortfall_minutes: number;
  late_days: number;
  late_minutes: number;
  early_days: number;
  early_minutes: number;
  /** Expected days with neither a scan nor approved leave. Not "absence" —
   *  nobody has established anyone was away; it is unaccounted for. */
  unaccounted_days: number;
  worked_hours: number;
  shortfall_hours: number;
  late_hours: number;
  early_hours: number;
}

export interface AttendanceAnalytics {
  range: { from: string; to: string; working_days: number };
  trust: AttendanceTrust;
  cost: {
    months: OvertimeMonth[];
    approved_minutes: number;
    pending_minutes: number;
    unsent_minutes: number;
    approved_hours: number;
    pending_hours: number;
    has_overtime: boolean;
  };
  time: AttendanceTimeUse;
  branches: AttendanceBranchRow[];
}

export const getAnalytics = (from: string, to: string) =>
  data<AttendanceAnalytics>(gatewayApi.get(`${BASE}/analytics`, { params: { from, to } }));

// ---- Overtime --------------------------------------------------------------

export const listOvertime = (status: OvertimeStatus | 'all' = 'pending') =>
  data<{ overtime_requests?: OvertimeRequest[]; requests?: OvertimeRequest[] } | OvertimeRequest[]>(
    gatewayApi.get(`${BASE}/overtime`, { params: { status } }),
  ).then((r) => (Array.isArray(r) ? r : (r.overtime_requests ?? r.requests ?? [])));

export const approveOvertime = (id: string, minutes?: number) =>
  data<OvertimeRequest & { sent_to_payroll?: boolean }>(
    gatewayApi.post(`${BASE}/overtime/${id}/approve`, minutes != null ? { minutes } : {}),
  );

export const rejectOvertime = (id: string, reason: string) =>
  data<OvertimeRequest>(gatewayApi.post(`${BASE}/overtime/${id}/reject`, { reason }));

// ---- Policy ----------------------------------------------------------------

export const getPolicy = () => data<AttendancePolicy>(gatewayApi.get(`${BASE}/policy`));

export const updatePolicy = (body: Partial<AttendancePolicy>) =>
  data<AttendancePolicy>(gatewayApi.put(`${BASE}/policy`, body));

// ---- Capture modes + self check-in (scanner-less offices) -------------------

export type CaptureMode = 'biometric' | 'self_checkin' | 'untracked';

/** Office-network check for self check-in: off, flag (accept + mark), or enforce. */
export type NetworkCheck = 'off' | 'flag' | 'enforce';

export interface CapturePolicyRow {
  id: string;
  branch_id: string | null; // null = tenant default
  mode: CaptureMode;
  network_check?: NetworkCheck;
  /** The office's PUBLIC internet address(es): single IPs or CIDR ranges. */
  office_networks?: string[];
  /** Scanner offices only: self check-in allowed as a BACKUP (default off). */
  self_checkin_backup?: boolean;
  /** When / by whom backup was turned on (null when off). */
  self_checkin_backup_since?: string | null;
  self_checkin_backup_by?: string | null;
  self_checkin_backup_days?: number | null;
  updated_at?: string | null;
}

export const listCapturePolicies = () =>
  data<{ default_mode: CaptureMode; default_self_checkin_backup?: boolean; policies: CapturePolicyRow[] }>(
    gatewayApi.get(`${BASE}/capture-policies`),
  );

export const upsertCapturePolicy = (
  branchId: string | null, mode: CaptureMode,
  network?: { network_check: NetworkCheck; office_networks: string[] },
  /** `base_updated_at`: the `updated_at` of this scope's row as last read
   *  (null = no row yet). The server refuses (409) a write made from a view
   *  that is no longer current. Omitted = no check. */
  extra?: { self_checkin_backup?: boolean; base_updated_at?: string | null },
) =>
  data<CapturePolicyRow>(
    gatewayApi.put(`${BASE}/capture-policies`, { branch_id: branchId, mode, ...(network ?? {}), ...(extra ?? {}) }),
  );

/** The address KaziPeople sees this computer at ("Use this computer's address"). */
export const getMyNetwork = () =>
  data<{ ip: string | null; public: boolean }>(gatewayApi.get(`${BASE}/network/me`));

export interface SelfDay {
  id: string;
  work_date: string;
  clock_in: string | null;
  clock_out: string | null;
  net_minutes: number;
  status: DayStatus;
  source: string;
  /** The day carries scanner punches (device / merged / file). Its clock_out
   *  is then the scanner's LAST punch — "last seen", not "left". */
  scanned?: boolean;
  /** When the scanner first saw them today (HH:MM local). */
  scanner_first?: string | null;
  /** Their OWN check-in / check-out (HH:MM local), if any. */
  self_in?: string | null;
  self_out?: string | null;
}

export const selfToday = () =>
  data<{ mode: CaptureMode | 'none'; backup?: boolean; work_date?: string; day: SelfDay | null;
    /** Their own check-in today, not yet checked out — Check out stays offered. */
    open_session?: boolean }>(
    gatewayApi.get(`${BASE}/self/today`),
  );

export const selfCheckIn = () =>
  data<{ status: string; day: SelfDay }>(gatewayApi.post(`${BASE}/self/check-in`));

export const selfCheckOut = () =>
  data<{ status: string; day: SelfDay }>(gatewayApi.post(`${BASE}/self/check-out`));

/** One of MY approved field-assignment days as attendance counted it, with
 *  the overtime it raised (null when within the standard day / not raised). */
export interface MyFieldDay {
  assignment_id: string;
  work_date: string;
  worked_minutes: number;
  outcome: string;
  overtime: {
    id: string;
    status: 'pending' | 'approved' | 'rejected';
    minutes_requested: number;
    minutes_approved: number | null;
    sent_to_payroll: boolean;
  } | null;
}

export const myFieldDays = (assignmentIds: string[]) =>
  data<{ field_days: MyFieldDay[] }>(
    gatewayApi.get(`${BASE}/self/field-days`, { params: { assignment_ids: assignmentIds.join(',') } }),
  ).then((r) => r.field_days ?? []);

/** Normalized days across batches (admin review). */
export const listDays = (params: { status?: DayStatus; from?: string; to?: string } = {}) =>
  data<{ days: AttendanceDay[] }>(gatewayApi.get(`${BASE}/days`, { params })).then((r) => r.days ?? []);

/** Approve one BATCHLESS day (self check-in / manual). */
export const approveDay = (dayId: string) =>
  data<{ status: string; overtime_request_created: boolean }>(
    gatewayApi.post(`${BASE}/days/${dayId}/approve`),
  );

// ---- Upload reminder settings ---------------------------------------------
//
// Who gets told when attendance is not current, and on which day. The HOUR and
// TIMEZONE are deliberately not here: scheduler-service's job row owns the
// daily trigger and is tuned in the Automation settings. This screen owns the
// recipients, the habit day and how far behind counts.

export type ReminderRole = 'to' | 'cc';

export interface ReminderRecipient {
  email: string;
  /** 'to' is expected to act; 'cc' is copied for visibility. */
  role: ReminderRole;
}

export interface ReminderSettings {
  enabled: boolean;
  recipients: ReminderRecipient[];
  /** 0 = Monday … 6 = Sunday. The tenant's habit day, not a rule. */
  nudge_weekday: number;
  /** Days behind before it fires. 1 = any gap at all. */
  stale_after_days: number;
  updated_at?: string | null;
}

export const getReminderSettings = () =>
  data<ReminderSettings>(gatewayApi.get(`${BASE}/reminder-settings`));

export const updateReminderSettings = (body: Partial<ReminderSettings>) =>
  data<ReminderSettings>(gatewayApi.put(`${BASE}/reminder-settings`, body));

// ---- Live BioTime connector (migration-service owns connections) ----------


export interface BioTimeConnector {
  connection_id: string;
  name: string;
  status: string;
  /** Bound to a source profile with a terminal timezone. */
  enrolled: boolean;
  timezone?: string;
  /** null when the status could not be fetched — never read that as healthy. */
  health: ConnectorStatus | null;
  health_error?: string;
}

export const listBioTimeConnectors = () =>
  data<{ connections: BioTimeConnector[] }>(gatewayApi.get('/api/v1/integrations/biotime/connectors'))
    .then((r) => r.connections ?? []);

export interface EnrolResult {
  connection_id: string;
  source_profile_id: string;
  timezone: string;
  measurement_start_date: string;
  /** Non-empty when the go-live date did NOT save. */
  warning?: string;
}

export const enrolBioTime = (body: { connection_id: string; timezone: string; measurement_start_date: string }) =>
  data<EnrolResult>(gatewayApi.post('/api/v1/integrations/biotime/enroll', body));

export interface PairingCode {
  /** XXXXX-XXXXX — read aloud on the install call, typed into the installer. */
  code: string;
  expires_at: string;
  expires_in_minutes: number;
}

export const createInstallerCode = (connectionId: string) =>
  data<PairingCode>(gatewayApi.post(`/api/v1/integrations/biotime/connectors/${connectionId}/pairing-code`));


/** One employee-day, everything behind its number (P2). Scoped server-side
 *  exactly like /attendance/days; an employee may open only their own. */
export interface AttendanceDayDetail {
  employee_id: string;
  work_date: string;
  timezone: string;
  source: string | null;
  punches: Array<{
    time: string; at: string; source: 'scanner' | 'self'; device: string;
    direction: string | null; received_at: string | null; received_late: boolean;
    network: string | null; late_arrival: boolean;
  }>;
  policy: {
    work_start: string | null; work_end: string | null; grace_minutes: number;
    standard_daily_minutes: number; lunch_minutes: number; lunch_after_minutes: number;
    lunch_paid: boolean; auto_lunch_deduct: boolean; overtime_daily_cap_minutes: number;
    overnight_mode: string | null;
  };
  era: { measured_from: string | null; late_and_ot_withheld: boolean };
  breakdown: {
    first_in: string | null; last_out: string | null; gross_minutes: number; break_minutes: number;
    break_rule: string; pairing: string | null; time_away_minutes: number; gaps_minutes: number[];
    net_minutes: number; regular_minutes: number; potential_ot_minutes: number;
    approved_ot_minutes: number; late_minutes: number; early_departure_minutes: number;
    day_kind: string | null;
  };
  self_check_in: { in_network: string | null; out_network: string | null } | null;
  exceptions: Array<{ id: string; type: string; severity: string; message: string; status: string;
    resolution_note: string | null; created_at: string | null }>;
  correction: { reason: string | null; by: string | null; at: string | null } | null;
  approval: {
    day_status: string | null;
    overtime: { status: string; minutes_requested: number; minutes_approved: number | null;
      approved_at: string | null; sent_to_payroll_at: string | null } | null;
  };
  field_days: Array<{ assignment_id: string; worked_minutes: number; outcome: string }>;
}

export const attendanceDayDetail = (employeeId: string, date: string) =>
  data<AttendanceDayDetail>(gatewayApi.get(`${BASE}/day-detail`, { params: { employee_id: employeeId, date } }));

// ── Multi-entity G6: which employment an attendance day belongs to ───────────
export interface AttendanceEngagementRule {
  id: string;
  employee_id: string;
  engagement_id: string;
  legal_entity_id: string | null;
  company_name: string | null;
  device_id: string | null;
  weekdays: number[];
  note: string | null;
}
export const listAttendanceEngagementRules = async (employeeId: string): Promise<AttendanceEngagementRule[]> =>
  (await gatewayApi.get(`${BASE}/engagement-rules`, { params: { employee_id: employeeId } })).data.rules ?? [];
export const listAttendanceSites = async (): Promise<{ device_id: string; name: string }[]> =>
  (await gatewayApi.get(`${BASE}/engagement-rules/sites`)).data.sites ?? [];
export const createAttendanceEngagementRule = async (body: {
  employee_id: string; engagement_id: string; device_id?: string | null; weekdays?: number[]; note?: string;
}): Promise<AttendanceEngagementRule> => (await gatewayApi.post(`${BASE}/engagement-rules`, body)).data;
export const deleteAttendanceEngagementRule = async (id: string) =>
  (await gatewayApi.delete(`${BASE}/engagement-rules/${id}`)).data;
